/**
 * Job detail — one job, four buttons (Pass on · Send back · Ask Michael ·
 * Waiting on client), handoff note history (read-only, newest first) and a
 * highlighted "What [previous person] said" box.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, Undo2, Flag, Pause, Play, CheckCircle2,
    Loader2, MessageSquare, History,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const JOB_TYPE_LABEL = {
    income_tax: "Income Tax Return", gst: "GST Return",
    payroll: "Payroll", others: "Others",
};
const STATUS_LABEL = {
    to_do: "To do", in_progress: "In progress",
    waiting_on_client: "Waiting on client", done: "Done",
};
const STATUS_COLOR = {
    to_do: "bg-zinc-50 text-zinc-700 border-zinc-200",
    in_progress: "bg-blue-50 text-blue-800 border-blue-200",
    waiting_on_client: "bg-amber-50 text-amber-900 border-amber-200",
    done: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const QUICK_NOTES = ["Recon done", "Checked OK", "Info missing, see below", "Ready to file"];
const ASK_MICHAEL_REASONS = [
    ["client_disputes_amount", "Client disputes amount or treatment"],
    ["client_cannot_pay", "Client cannot pay"],
    ["client_asks_for_advice", "Client asks for tax advice"],
    ["complaint", "Complaint"],
    ["change_to_filed_return", "Change to a filed return"],
    ["records_do_not_agree", "Records do not agree"],
    ["no_approved_template", "No approved template fits"],
    ["unsure", "Unsure"],
];

export default function JobDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";

    const [job, setJob] = useState(null);
    const [history, setHistory] = useState([]);
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);

    const [passOpen, setPassOpen] = useState(false);
    const [sendBackOpen, setSendBackOpen] = useState(false);
    const [askOpen, setAskOpen] = useState(false);
    const [waitOpen, setWaitOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [{ data: j }, { data: h }, { data: t }] = await Promise.all([
                api.get(`/job-flow/jobs/${id}`),
                api.get(`/job-flow/jobs/${id}/history`),
                api.get(`/job-flow/team`),
            ]);
            setJob(j);
            setHistory((h || []).slice().reverse()); // newest first
            setTeam(t || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="py-10 text-center text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>;
    if (!job) return <div className="p-4"><p className="text-[#5E636E]">Job not found.</p></div>;

    const pipeline = job.pipeline?.steps || [];
    const currentStep = pipeline[job.current_step_index] || null;
    const isOwner = job.current_owner_user_id === user?.id;
    const canActNormal = isOwner || isAdmin;
    const canCompleteAdminStep = isAdmin;
    const canSendBack = canActNormal && job.current_step_index > 0;
    const isTerminal = job.status === "done";
    const latest = history[0] || null;
    const title = job.type === "others" ? (job.title || "Untitled") : JOB_TYPE_LABEL[job.type];
    const period = job.type === "income_tax" ? job.tax_year : (job.period_start && job.period_end ? `${job.period_start} → ${job.period_end}` : "");

    async function doStart() {
        try { await api.post(`/job-flow/jobs/${id}/start`); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }
    async function doClientReplied() {
        try { await api.post(`/job-flow/jobs/${id}/client-replied`, {}); await load(); toast.success("Marked as in progress"); }
        catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="space-y-4" data-testid="jf-detail">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <button onClick={() => navigate("/app/job-flow")} className="text-[12px] text-[#5E636E] hover:text-[#0F1115] inline-flex items-center gap-1" data-testid="jf-back">
                        <ArrowLeft className="h-3 w-3" /> Back to Job Flow
                    </button>
                    <h1 className="text-xl font-semibold text-[#0F1115] mt-1">
                        {job.client?.name || "Internal"}
                    </h1>
                    <div className="text-[12.5px] text-[#5E636E]">
                        {title}{period ? ` · ${period}` : ""}{job.due_date ? ` · due ${job.due_date}` : ""}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[job.status]}`}>
                        {STATUS_LABEL[job.status]}
                    </span>
                    {job.needs_michael && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-semibold px-2 py-0.5">
                            <Flag className="h-3 w-3" /> Needs Michael
                        </span>
                    )}
                </div>
            </header>

            {/* Step strip */}
            <section className="rounded-md border border-[#E4E4E7] bg-white p-3">
                <div className="flex flex-wrap gap-1.5" data-testid="jf-step-strip">
                    {pipeline.map((s, i) => {
                        const past = i < job.current_step_index;
                        const now = i === job.current_step_index;
                        return (
                            <div key={s.key}
                                className={`text-[11.5px] px-2 py-1 rounded-md border ${now ? "bg-[#0F1115] text-white border-[#0F1115]" : past ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-white text-[#5E636E] border-[#E4E4E7]"}`}>
                                {i + 1}. {s.label}{s.admin_only ? " 🔒" : ""}
                            </div>
                        );
                    })}
                </div>
                <div className="mt-2 text-[11.5px] text-[#5E636E]">
                    Current owner: <b className="text-[#0F1115]">{job.owner?.name || "—"}</b>
                    {currentStep?.admin_only && <span className="ml-2 text-amber-700">· Admin-only step</span>}
                </div>
            </section>

            {/* What previous person said */}
            {latest && (
                <section className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="jf-latest-note">
                    <div className="text-[11px] uppercase text-amber-900 font-semibold tracking-wide mb-1 flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> What {latest.from_user_name || latest.created_by_name || "someone"} said
                    </div>
                    <div className="text-[13px] text-[#0F1115] whitespace-pre-wrap">{latest.note || latest.reason || "(no note)"}</div>
                    <div className="text-[11px] text-[#5E636E] mt-1">
                        {new Date(latest.created_at).toLocaleString("en-NZ")} · {latest.action.replace(/_/g, " ")}
                    </div>
                </section>
            )}

            {/* Checklist for current step */}
            {currentStep?.checklist?.length > 0 && (
                <section className="rounded-md border border-[#E4E4E7] bg-white p-3">
                    <div className="text-[12.5px] font-medium text-[#0F1115] mb-1.5">Checklist for this step</div>
                    <ul className="list-disc pl-5 space-y-0.5">
                        {currentStep.checklist.map((c, i) => <li key={i} className="text-[12.5px] text-[#5E636E]">{c}</li>)}
                    </ul>
                </section>
            )}

            {/* Action buttons */}
            {!isTerminal && (
                <section className="flex flex-wrap gap-2" data-testid="jf-actions">
                    {job.status === "to_do" && isOwner && (
                        <Button variant="outline" onClick={doStart} data-testid="jf-start">
                            <Play className="h-3.5 w-3.5 mr-1" /> Start working
                        </Button>
                    )}
                    {job.status === "waiting_on_client" && isOwner && (
                        <Button variant="outline" onClick={doClientReplied} data-testid="jf-client-replied">
                            <Play className="h-3.5 w-3.5 mr-1" /> Client replied
                        </Button>
                    )}
                    {canActNormal && (
                        <>
                            <Button onClick={() => setPassOpen(true)} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-pass-on">
                                <ArrowRight className="h-3.5 w-3.5 mr-1" /> Pass on
                            </Button>
                            {canSendBack && (
                                <Button variant="outline" onClick={() => setSendBackOpen(true)} data-testid="jf-send-back">
                                    <Undo2 className="h-3.5 w-3.5 mr-1" /> Send back
                                </Button>
                            )}
                            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => setAskOpen(true)} data-testid="jf-ask-michael">
                                <Flag className="h-3.5 w-3.5 mr-1" /> Ask Michael
                            </Button>
                            {job.status !== "waiting_on_client" && (
                                <Button variant="outline" onClick={() => setWaitOpen(true)} data-testid="jf-waiting">
                                    <Pause className="h-3.5 w-3.5 mr-1" /> Waiting on client
                                </Button>
                            )}
                        </>
                    )}
                    {!canCompleteAdminStep && currentStep?.admin_only && isOwner && (
                        <div className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                            This step is Admin-only — Pass on to Michael or another admin.
                        </div>
                    )}
                </section>
            )}

            {/* Full history */}
            <section className="rounded-md border border-[#E4E4E7] bg-white">
                <header className="px-4 py-2 border-b border-[#EEEDE7] text-[13px] font-medium flex items-center gap-1">
                    <History className="h-3.5 w-3.5" /> Full history ({history.length})
                </header>
                {history.length === 0 ? (
                    <div className="p-4 text-[12.5px] text-[#5E636E]">No activity yet.</div>
                ) : (
                    <ol className="divide-y divide-[#EEEDE7]">
                        {history.map((h) => (
                            <li key={h.id} className="px-4 py-2">
                                <div className="text-[11px] text-[#5E636E]">
                                    {new Date(h.created_at).toLocaleString("en-NZ")} · <b className="text-[#0F1115]">{h.action.replace(/_/g, " ")}</b>
                                </div>
                                <div className="text-[12.5px] text-[#0F1115]">
                                    <b>{h.from_user_name || h.created_by_name || "—"}</b>
                                    {h.to_user_name ? <> → <b>{h.to_user_name}</b></> : null}
                                    {h.from_step !== h.to_step && h.from_step != null && h.to_step != null ? <> (step {h.from_step + 1} → {h.to_step + 1})</> : null}
                                </div>
                                {(h.note || h.reason) && (
                                    <div className="mt-1 text-[12.5px] text-[#334155] whitespace-pre-wrap">{h.note || h.reason}</div>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </section>

            {passOpen && (
                <PassOnDialog open onClose={() => setPassOpen(false)} job={job} pipeline={pipeline} team={team}
                    onDone={async () => { setPassOpen(false); await load(); }} />
            )}
            {sendBackOpen && (
                <ReasonDialog open onClose={() => setSendBackOpen(false)} title="Send back"
                    label="Why are you sending it back?" minLen={5}
                    onSubmit={async (reason) => {
                        try { await api.post(`/job-flow/jobs/${id}/send-back`, { reason }); setSendBackOpen(false); await load(); toast.success("Sent back"); }
                        catch (e) { toast.error(formatApiError(e)); }
                    }} testid="jf-send-back-dialog" />
            )}
            {askOpen && (
                <AskMichaelDialog open onClose={() => setAskOpen(false)}
                    onSubmit={async (reason, note) => {
                        try { await api.post(`/job-flow/jobs/${id}/ask-michael`, { reason, note: note || null }); setAskOpen(false); await load(); toast.success("Sent to Michael"); }
                        catch (e) { toast.error(formatApiError(e)); }
                    }} />
            )}
            {waitOpen && (
                <ReasonDialog open onClose={() => setWaitOpen(false)} title="Waiting on client"
                    label="What are we waiting for?" minLen={5}
                    onSubmit={async (note) => {
                        try { await api.post(`/job-flow/jobs/${id}/waiting-on-client`, { note }); setWaitOpen(false); await load(); toast.success("Marked waiting on client"); }
                        catch (e) { toast.error(formatApiError(e)); }
                    }} testid="jf-waiting-dialog" />
            )}
        </div>
    );
}

function PassOnDialog({ open, onClose, job, pipeline, team, onDone }) {
    const atLast = job.current_step_index >= (pipeline.length - 1);
    const nextStep = pipeline[job.current_step_index + 1] || null;
    const [note, setNote] = useState("");
    const [toUserId, setToUserId] = useState(nextStep?.default_owner_user_id || "");
    const [busy, setBusy] = useState(false);

    const eligible = useMemo(() => {
        if (!nextStep?.admin_only) return team;
        return team.filter((u) => u.role === "firm_admin" || u.role === "super_admin");
    }, [team, nextStep]);

    async function submit() {
        if (!note || note.trim().length < 5) { toast.error("Please add a note (5+ chars)."); return; }
        setBusy(true);
        try {
            if (atLast) {
                // Last step: pass-on becomes "mark done" per brief for Others and generally
                await api.post(`/job-flow/jobs/${job.id}/mark-done`, { note });
            } else {
                if (!toUserId) { toast.error("Pick a person"); setBusy(false); return; }
                await api.post(`/job-flow/jobs/${job.id}/pass-on`, { note, to_user_id: toUserId });
            }
            toast.success(atLast ? "Marked as done" : "Passed on");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="jf-pass-dialog">
                <DialogHeader><DialogTitle>{atLast ? (job.type === "others" ? "Mark as done or pick someone?" : "Finish and mark as done") : "Pass on to next step"}</DialogTitle></DialogHeader>
                {!atLast && (
                    <div className="space-y-2">
                        <div className="text-[12.5px] text-[#5E636E]">
                            Next step: <b className="text-[#0F1115]">{nextStep?.label}</b>{nextStep?.admin_only ? " (Admin only)" : ""}
                        </div>
                        <Label>Assign to</Label>
                        <select value={toUserId} onChange={(e) => setToUserId(e.target.value)}
                            className="w-full h-8 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px]" data-testid="jf-pass-to">
                            <option value="">— Pick —</option>
                            {eligible.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.role === "firm_admin" ? " (Admin)" : ""}</option>)}
                        </select>
                    </div>
                )}
                <div className="mt-2">
                    <Label>Note (required, 5+ chars)</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {QUICK_NOTES.map((q) => (
                            <button key={q} onClick={() => setNote((n) => n ? `${n}\n${q}` : q)}
                                className="text-[11.5px] px-2 py-0.5 rounded-full border border-[#E4E4E7] bg-zinc-50 hover:border-[#0F1115]">
                                {q}
                            </button>
                        ))}
                    </div>
                    <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                        className="mt-1 text-[12.5px]" placeholder="A short handoff note…" data-testid="jf-pass-note" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-pass-submit">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                        {atLast ? "Mark done" : "Pass on"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AskMichaelDialog({ open, onClose, onSubmit }) {
    const [reason, setReason] = useState("unsure");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="jf-ask-dialog">
                <DialogHeader><DialogTitle>Ask Michael</DialogTitle></DialogHeader>
                <Label>Reason</Label>
                <select value={reason} onChange={(e) => setReason(e.target.value)}
                    className="w-full h-8 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px] mt-1" data-testid="jf-ask-reason">
                    {ASK_MICHAEL_REASONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <Label className="mt-2">Note (optional)</Label>
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    className="mt-1 text-[12.5px]" data-testid="jf-ask-note" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={async () => { setBusy(true); await onSubmit(reason, note); setBusy(false); }} disabled={busy}
                        className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-ask-submit">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Send to Michael
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReasonDialog({ open, onClose, title, label, minLen = 5, onSubmit, testid }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid={testid}>
                <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
                <Label>{label}</Label>
                <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
                    className="mt-1 text-[12.5px]" data-testid={`${testid}-text`} />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={async () => {
                        if (!text || text.trim().length < minLen) { toast.error(`Please add a note (${minLen}+ chars).`); return; }
                        setBusy(true); await onSubmit(text); setBusy(false);
                    }} disabled={busy} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid={`${testid}-submit`}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Submit
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
